import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceArea, Legend } from "recharts";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { ArrowLeft, GitCompareArrows } from "lucide-react";

const COMPARE_PRESETS = [
  ["systolic_bp", "weight"],
  ["hba1c", "weight"],
  ["ldl_cholesterol", "total_cholesterol"],
  ["fasting_glucose", "hba1c"],
];

export default function ObservationDetail() {
  const { canonical_type } = useParams();
  const navigate = useNavigate();
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [compareWith, setCompareWith] = useState(null);
  const [compareSeries, setCompareSeries] = useState(null);

  useEffect(() => {
    setLoading(true);
    setCompareWith(null);
    setCompareSeries(null);
    (async () => {
      try {
        const { data } = await api.get(`/observations/${canonical_type}/context`);
        setCtx(data);
      } finally { setLoading(false); }
    })();
  }, [canonical_type]);

  useEffect(() => {
    if (!compareWith) { setCompareSeries(null); return; }
    (async () => {
      try {
        const { data } = await api.get(`/observations/${compareWith}/context`);
        setCompareSeries(data);
      } catch { setCompareSeries(null); }
    })();
  }, [compareWith]);

  const mergedData = useMemo(() => {
    const primary = ctx?.points || ctx?.series || [];
    const secondary = compareSeries?.points || compareSeries?.series || [];
    const byDate = new Map();
    primary.forEach((p) => byDate.set(p.date, { date: p.date, v1: p.value }));
    secondary.forEach((p) => {
      const cur = byDate.get(p.date) || { date: p.date };
      cur.v2 = p.value;
      byDate.set(p.date, cur);
    });
    return Array.from(byDate.values()).sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  }, [ctx, compareSeries]);

  if (loading) return <div className="space-y-4"><Skeleton className="h-8 w-40" /><Skeleton className="h-64 w-full" /></div>;
  if (!ctx) return <div className="text-neutral-500">No data.</div>;

  const title = ctx.title || canonical_type.replace(/_/g, " ");
  const unit = ctx.unit || "";
  const ref = ctx.reference;
  const color = ctx.color || "#F6CFBE";
  const secondaryColor = "#B9DCF2";
  const primaryPoints = ctx.points || ctx.series || [];

  return (
    <div className="space-y-6" data-testid="observation-detail">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-900">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <div>
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight capitalize">{title}</h1>
        <p className="text-neutral-600 mt-1 text-sm">{primaryPoints.length} readings {unit ? `· ${unit}` : ""}</p>
      </div>

      <div className="bg-white rounded-2xl border border-neutral-200 p-4">
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={mergedData} margin={{ top: 10, right: 8, left: 0, bottom: 5 }}>
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="left" tick={{ fontSize: 11 }} width={40} />
              {compareWith && <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} width={40} />}
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e5e5e5" }} />
              {ref && ref.low != null && ref.high != null && (
                <ReferenceArea yAxisId="left" y1={ref.low} y2={ref.high} fill={color} fillOpacity={0.12} strokeOpacity={0} />
              )}
              <Line yAxisId="left" type="monotone" dataKey="v1" name={title} stroke={color} strokeWidth={2.5} dot={{ r: 3 }} />
              {compareWith && (
                <Line yAxisId="right" type="monotone" dataKey="v2" name={compareWith.replace(/_/g, " ")} stroke={secondaryColor} strokeWidth={2.5} dot={{ r: 3 }} />
              )}
              {compareWith && <Legend />}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <section>
        <div className="flex items-center gap-2 mb-2">
          <GitCompareArrows className="w-4 h-4 text-neutral-500" />
          <h2 className="text-sm uppercase tracking-wide text-neutral-500">Compare with</h2>
        </div>
        <div className="flex flex-wrap gap-2">
          {compareWith && (
            <Button data-testid="compare-clear" variant="outline" size="sm" onClick={() => setCompareWith(null)}>Clear</Button>
          )}
          {COMPARE_PRESETS
            .flatMap(([a, b]) => (a === canonical_type ? [b] : b === canonical_type ? [a] : []))
            .filter((t, i, arr) => arr.indexOf(t) === i)
            .map((t) => (
              <Button
                key={t}
                data-testid={`compare-${t}`}
                variant={compareWith === t ? "default" : "outline"}
                size="sm"
                onClick={() => setCompareWith(t)}
              >
                {t.replace(/_/g, " ")}
              </Button>
            ))}
        </div>
      </section>

      {ctx.context_events?.length > 0 && (
        <section>
          <h2 className="text-sm uppercase tracking-wide text-neutral-500 mb-2">Around this time</h2>
          <div className="space-y-2">
            {ctx.context_events.slice(0, 8).map((e) => (
              <button
                key={e.event_id}
                onClick={() => navigate(`/events/${e.event_id}`)}
                className="w-full text-left bg-white rounded-xl border border-neutral-200 p-3 hover:border-neutral-300"
              >
                <div className="font-medium text-neutral-900">{e.title}</div>
                <div className="text-xs text-neutral-500 mt-0.5">{e.date} · {e.type}</div>
              </button>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
